import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import type {BannerV2SliceCtaSingleImage} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {ResponsiveImage} from '../components/ResponsiveImage'

type CtaSingleImageProps = {
  slice: BannerV2SliceCtaSingleImage & {slice_type: string}
  index: number
  pageUid: string
}

export const CtaSingleImage = ({slice, index, pageUid}: CtaSingleImageProps) => {
  const {linkResolver} = useLinkResolver()

  return (
    <Container className="!py-6 px-5 md:!py-12 md:px-10 lg:px-5 xl:px-0">
      <div className="mx-auto flex w-full max-w-container overflow-hidden rounded-lg bg-green-dark max-lg:flex-col max-lg:gap-6 lg:justify-between lg:pl-8">
        <div className="flex flex-col gap-8 p-6 lg:justify-between lg:px-0 lg:py-8">
          <div className="flex flex-col gap-3 lg:max-w-[416px] lg:gap-2">
            <PrismicRichText
              field={slice.primary.tag}
              components={{
                paragraph: ({children}) => <span className="text-button-s uppercase text-green-neon">{children}</span>,
              }}
            />
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading1: ({children}) => (
                  <h3 className="text-[32px] font-medium leading-[normal] text-moss-white lg:leading-[38px]">
                    {children}
                  </h3>
                ),
              }}
            />
            <div className="lg:max-w-[326px]">
              <PrismicRichText
                field={slice.primary.description}
                components={{
                  paragraph: ({children}) => <p className="text-tg-body-m text-moss-white/[70%]">{children}</p>,
                  list: ({children}) => <ul className="flex list-none flex-col gap-[14px] py-8">{children}</ul>,
                  listItem: ({children}) => (
                    <li className="flex items-start gap-3">
                      <div className="mt-[3px] flex size-3 min-h-3 min-w-3 items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                          <rect y="0.865234" width="12" height="12" rx="2" fill="white" fill-opacity="0.3" />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.93265 4.71086C9.17604 4.95425 9.17604 5.34886 8.93265 5.59225L5.50484 9.02006C5.26145 9.26345 4.86684 9.26345 4.62345 9.02006L3.06535 7.46197C2.82197 7.21858 2.82197 6.82397 3.06535 6.58058C3.30874 6.33719 3.70336 6.33719 3.94675 6.58058L5.06415 7.69798L8.05126 4.71086C8.29465 4.46747 8.68926 4.46747 8.93265 4.71086Z"
                            fill="#FEFEFE"
                          />
                        </svg>
                      </div>
                      <p className="text-tg-body-l leading-[normal] text-moss-white">{children}</p>
                    </li>
                  ),
                  strong: ({children}) => <span className="block text-moss-white/[70%]">{children}</span>,
                }}
              />
            </div>
          </div>
          <div>
            {isFilled.link(slice.primary.button_link) && (
              <ButtonV2
                variant="primary"
                href={linkResolver(slice.primary.button_link)}
                trackingData={{
                  pageName: pageUid,
                  location: `${slice.slice_type}-${index}`,
                  dataType: 'primary-button',
                  mktgId: slice.primary.button_marketing_id || '',
                }}
                className="w-fit"
              >
                {slice.primary.button_link.text}
              </ButtonV2>
            )}
            {isFilled.richText(slice.primary.footer) && (
              <div className="pt-4">
                <PrismicRichText
                  field={slice.primary.footer}
                  components={{
                    paragraph: ({children}) => <p className="text-button-s text-moss-white/[70%]">{children}</p>,
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <ResponsiveImage
          image={slice.primary.image}
          pictureClassName="w-full max-lg:pl-6 lg:aspect-[73/56] lg:max-w-[50%]"
          mobileClassName="lg:hidden"
          desktopClassName=""
        />
      </div>
    </Container>
  )
}
